import * as React from 'react';
import { DEFAULT_ORIGIN } from './schema';

/**
 * JsonLd
 * --------------------------------------------------------------
 * Injects a `<script type="application/ld+json">` element into the
 * document <head> for a schema.org structured-data node (or array of
 * nodes). Effect-based — no head-manager dependency, matching the
 * imperative approach already used by MarketingPageHead.
 *
 * Each instance owns exactly one script tag, keyed by `id`, and removes
 * it on unmount / re-navigate so structured data never leaks between
 * routes (e.g. a blog post's BlogPosting node must not linger on /pricing).
 *
 * Callers SHOULD memoize `schema` (useMemo) so the serialized string is
 * stable across renders; the effect only rewrites the DOM when the
 * serialized JSON actually changes.
 */
export function JsonLd({
  schema,
  id,
}: {
  schema: object | object[];
  id: string;
}): null {
  const json = React.useMemo(() => JSON.stringify(schema), [schema]);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const scriptId = `jsonld-${id}`;

    let el = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      el.setAttribute('data-worqly-jsonld', id);
      document.head.appendChild(el);
    }
    el.text = json;

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    };
  }, [id, json]);

  return null;
}

/**
 * Read the live document origin, falling back to the production origin
 * during SSR / tests where `window` is unavailable. Kept out of the pure
 * builder module so those stay DOM-free and unit-testable.
 */
export function siteOrigin(): string {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return DEFAULT_ORIGIN;
}
